/* HC custom — built-in single-store map for the location pages.
   Self-hosted Leaflet (assets/hc-leaflet.js) + free Carto basemap tiles and
   a brand-navy pin. Replaces a 1.4MB third-party locator widget for the
   one-pin job. Custom element so theme-editor morphs re-hydrate cleanly;
   the Leaflet lib is injected once, on the first map's connect.

   The pin carries an info popup (open by default, like the old widget):
   store name + address read live from the page's own head/card DOM — no
   duplicated data to maintain — plus a Get-directions link. */

(function () {
  var leafletPromise = null;

  function loadLeaflet(src) {
    if (window.L) return Promise.resolve();
    if (leafletPromise) return leafletPromise;
    leafletPromise = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.defer = true;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return leafletPromise;
  }

  var PIN_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="34" height="46" viewBox="0 0 34 46">' +
    '<path d="M17 1C8.2 1 1 8.1 1 16.9 1 28.9 17 45 17 45s16-16.1 16-28.1C33 8.1 25.8 1 17 1z" fill="#0F263D" stroke="#FCFBF9" stroke-width="1.5"/>' +
    '<circle cx="17" cy="16.5" r="5.5" fill="#FCFBF9"/></svg>';

  var TILES = {
    light: {
      url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
    },
    voyager: {
      url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
    },
  };

  function esc(s) {
    var d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  class HcMap extends HTMLElement {
    connectedCallback() {
      var el = this;
      if (el.dataset.hydrated) return;
      el.dataset.hydrated = '1';
      var lat = parseFloat(el.dataset.lat);
      var lng = parseFloat(el.dataset.lng);
      if (!isFinite(lat) || !isFinite(lng)) return;
      loadLeaflet(el.dataset.leafletSrc)
        .then(function () {
          var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          var map = L.map(el, {
            center: [lat, lng],
            zoom: parseInt(el.dataset.zoom || '15', 10),
            scrollWheelZoom: el.dataset.scrollZoom === 'true',
            fadeAnimation: !reduced,
            zoomAnimation: !reduced,
            // attribution renders under the map (liquid) unless the block
            // asks for it on the canvas - the "Leaflet" prefix never shows
            attributionControl: false,
            zoomControl: false,
          });
          L.control
            .zoom({ position: el.dataset.controls === 'left' ? 'topleft' : 'topright' })
            .addTo(map);
          var tiles = TILES[el.dataset.tiles] || TILES.light;
          if (el.dataset.attribution === 'map') {
            L.control
              .attribution({ prefix: false, position: 'bottomright' })
              .addAttribution(tiles.attribution)
              .addTo(map);
          }
          // Carto requires a key on every tile request (since 2026); without
          // it the tiles still load but carry an "API key required" watermark.
          // The key is set once in Theme settings > HC Location maps.
          var tileKey = (el.dataset.tileKey || '').trim();
          var tileUrl = tileKey
            ? tiles.url + '?key=' + encodeURIComponent(tileKey)
            : tiles.url;
          L.tileLayer(tileUrl, {
            maxZoom: 19,
          }).addTo(map);
          var icon = L.divIcon({
            className: 'hc-map__pin',
            html: PIN_SVG,
            iconSize: [34, 46],
            iconAnchor: [17, 45],
            popupAnchor: [0, -44],
          });
          var marker = L.marker([lat, lng], {
            icon: icon,
            keyboard: true,
            title: el.dataset.pinTitle || '',
            alt: el.dataset.pinTitle || 'Store location',
          }).addTo(map);

          var directionsUrl =
            'https://www.google.com/maps/dir/?api=1&destination=' + lat + ',' + lng;

          if (el.dataset.popup === 'true') {
            // store name + address come from the page itself (head + card)
            var name =
              (document.querySelector('.hclo-head__title') || {}).textContent ||
              el.dataset.pinTitle ||
              '';
            var addrEl = document.querySelector('.hclo-uaddr');
            var addr = addrEl
              ? addrEl.innerHTML
              : '';
            var html =
              '<div class="hc-map__card">' +
              (name ? '<p class="hc-map__card-name">' + esc(name.trim()) + '</p>' : '') +
              (addr ? '<p class="hc-map__card-addr">' + addr + '</p>' : '') +
              '<a class="hc-map__card-link" href="' + directionsUrl + '" target="_blank" rel="noopener">Get directions</a>' +
              '</div>';
            marker.bindPopup(html, {
              closeButton: true,
              autoClose: false,
              closeOnClick: false,
              autoPan: false,
              maxWidth: 260,
            });
            if (el.dataset.popupOpen === 'true') {
              marker.openPopup();
              // centre the pin+card ensemble, not the pin: shift the view up
              // by half the card's rendered height so the pair sits balanced
              var pop = el.querySelector('.leaflet-popup');
              var shift = pop ? Math.round(pop.offsetHeight / 2) : 60;
              var pt = map.project([lat, lng], map.getZoom()).subtract([0, shift]);
              map.setView(map.unproject(pt, map.getZoom()), map.getZoom(), { animate: false });
            }
          } else {
            marker.on('click', function () {
              window.open(directionsUrl, '_blank', 'noopener');
            });
          }

          if ('ResizeObserver' in window) {
            /* HC: keep references on the element so disconnectedCallback can
               tear both down. Leaflet registers its own window and pointer
               handlers and tile state, so an un-removed map is the heaviest
               leak of the three - only map.remove() releases it. */
            el._hcRo = new ResizeObserver(function () {
              map.invalidateSize();
            });
            el._hcRo.observe(el);
          }
          el._hcMap = map;
        })
        .catch(function () {
          /* library failed to load - the band simply stays empty */
        });
    }

    disconnectedCallback() {
      if (this._hcRo) {
        this._hcRo.disconnect();
        this._hcRo = null;
      }
      if (this._hcMap) {
        this._hcMap.remove();
        this._hcMap = null;
      }
      // let a re-inserted element hydrate again after a morph
      delete this.dataset.hydrated;
    }
  }

  if (!customElements.get('hc-map')) {
    customElements.define('hc-map', HcMap);
  }
})();
